import { Stack } from '@chakra-ui/layout';
import {
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  FormControl,
  FormLabel,
  MenuItem,
  Spinner,
  Text,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Textarea } from '~/components';
import { useSinister } from '~/hooks/useSinisters/useSinisters';
import { GeneralInformation } from '~/pages/Portal/Sinisters/GeneralInformation';
import apiSinister from '~/utils/apiSinister';
import { queryClient } from '~/utils/react-query';

export const SinistersCancelForm: React.FC<{
  sinisterId: number;
  link?: boolean;
}> = ({ sinisterId, link = false }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const btnRef = useRef(null);
  const toast = useToast();
  const {
    register,
    handleSubmit,
    setValue,
    reset,
    setError,
    formState,
    clearErrors,
    control,
  } = useForm();
  const { errors } = formState;
  const [isFetching, setFetching] = useState(false);

  const [descricao, setDescricao] = useState<string>('');
  const { data, isLoading, refetch } = useSinister(sinisterId);

  useEffect(() => {
    if (link) handleOpen();
  }, []);

  const handleOpen = () => {
    onOpen();
  };

  const handleClose = () => {
    clearValues();
    onClose();
  };
  const clearValues = () => {
    setDescricao('');
    clearErrors();
  };

  async function handleCreate() {
    clearErrors();

    (!descricao || !descricao.trim().length) &&
      setError('descricao', {
        type: 'required',
        message: 'Motivo é obrigatório',
      });

    descricao.length > 255 &&
      setError('descricao', {
        type: 'maxLength',
        message: 'Motivo com mais de 255 caracteres',
      });

    const { _formState } = control;
    if (!_formState.isValid) return;

    const form = {
      motivo: descricao,
    };

    setFetching(true);

    const request = apiSinister.put(`cancelar-sinistro/${data?.id}/`, form);

    request
      .then(() => {
        toast({
          title: `Sinistro cancelado`,
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
        clearValues();
        onClose();
        queryClient.invalidateQueries('sinistersList');
        queryClient.invalidateQueries(['sinisterLog', sinisterId]);
        queryClient.invalidateQueries(['sinister', sinisterId]);
        queryClient.invalidateQueries('sinisterDashboard');
      })
      .catch(error => {
        if (error.response.status === 403) {
          toast({
            title: 'Usuário sem permissão',
            description: 'Sem permissão para execução da operação.',
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        } else if (error.response.status === 413) {
          toast({
            title: `Tamanho de requisição excedida`,
            description: 'Limite máximo para tamanho de arquivos é de 5MB',
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        } else if (error.response.status === 400) {
          // erros de valid

          const errorsValidate = Object.entries(error.response.data);

          errorsValidate.forEach(err => {
            console.log(err[0]);
            toast({
              title: `Erros ao cancelar sinistro`,
              description: String(err[1]),
              status: 'error',
              duration: 3000,
              isClosable: true,
              position: 'top-right',
            });
          });
        } else {
          toast({
            title: `Erro ao cancelar sinistro`,
            description: 'Tente novamente',
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        }
      })
      .finally(() => {
        setFetching(false);
      });
  }

  function handleDescricao(e: any) {
    setDescricao(e.target.value);
    if (e.target.value.length <= 255) {
      clearErrors('descricao');
    }
  }

  return (
    <>
      <MenuItem onClick={handleOpen}>Cancelar sinistro</MenuItem>
      <Drawer
        isOpen={isOpen}
        placement="right"
        onClose={handleClose}
        size="lg"
        closeOnOverlayClick={!isFetching}
      >
        <DrawerOverlay />
        <DrawerContent borderTopLeftRadius="md">
          <DrawerCloseButton isDisabled={isFetching} />
          <DrawerHeader>Cancelar sinistro</DrawerHeader>
          <DrawerBody>
            <Stack gridGap={6} bg="white" maxW={600} w="100%">
              <Stack gridGap={4}>
                <GeneralInformation sinister={data!} isAdmin />
                <FormControl isInvalid={errors.descricao}>
                  <FormLabel
                    htmlFor="writeUpFile"
                    style={{ fontSize: '14px' }}
                    justifyContent="space-between"
                    display="flex"
                  >
                    Entre com a justificativa *
                  </FormLabel>
                  <Textarea
                    name="descricao"
                    resize="none"
                    error={errors?.descricao}
                    value={descricao}
                    onChange={e => handleDescricao(e)}
                    fontSize="15px"
                    placeholder="Entre com a justificativa"
                    isDisabled={isFetching}
                  />
                </FormControl>
              </Stack>
            </Stack>
            <Button
              type="submit"
              onClick={handleSubmit(handleCreate)}
              color="white"
              background="gray.900"
              _hover={{
                bgColor: 'black',
              }}
              style={{ marginTop: '20px' }}
              isDisabled={isFetching}
            >
              Confirmar{' '}
              {isFetching && <Spinner style={{ marginLeft: '10px' }} />}
            </Button>
            <Button
              type="submit"
              onClick={handleClose}
              color="black"
              background="transparent"
              style={{
                marginTop: '20px',
                marginLeft: '10px',
              }}
              isDisabled={isFetching}
            >
              Cancelar
            </Button>
            <Text
              style={{ fontSize: '12px', color: 'gray.500', marginTop: '20px' }}
            >
              * Campos obrigatórios
            </Text>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );
};
